import Swiper from '@theme/swiper';
import { responsive } from '@theme/responsive';
import { trapFocus, removeTrapFocus } from '@theme/focus-trap';

class ProductGallery extends HTMLElement {
  #photoswipe = null;
  #pswpTiles = [];
  #mlb = null;
  #mlbObserver = null;
  #mlbOpener = null;
  #tiles = [];
  #showMoreEl = null;
  #track = null;
  #host = null;
  #swiper = null;
  #swiperEl = null;
  #videoVisibility = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    this.#tiles = Array.from(this.querySelectorAll('.main-product__gallery-tile'));
    this.#showMoreEl = this.querySelector('[data-show-more]');
    this.#swiperEl = this.querySelector('[data-gallery-swiper]');
    this.#host = this.closest('main-product');

    this.#tiles.forEach((tile) => {
      tile.addEventListener('click', this.#onTileClick);
      tile.addEventListener('keydown', this.#onTileKeydown);
    });
    this.#applyRovingTabindex(this.#tiles[0]);
    this.#showMoreEl?.addEventListener('click', this.#onShowMoreClick);
    this.#host?.addEventListener('pdp:variant-update', this.#onVariantUpdate);

    responsive.addBreakpointListener('pocket');
    document.body.addEventListener('breakpoint:pocket', this.#onBreakpointChange);
    if (responsive.matchesBreakpoint('pocket')) this.#initSwiper();

    this.#observeInlineVideos();
  }

  // Inline gallery videos (muted) play while in view and pause when out, on both
  // breakpoints: desktop autoplays on arrival (the tile is in the on-page grid),
  // mobile plays once the user swipes the off-screen slide into view.
  #observeInlineVideos() {
    // "Autoplay" (section setting) off -> inline gallery videos stay on their poster
    // until opened in the lightbox.
    if (this.dataset.videoAutoplay === 'false') return;
    const videos = this.querySelectorAll('.main-product__gallery-video');
    if (!videos.length) return;

    this.#videoVisibility = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.play?.().catch(() => {});
          else entry.target.pause?.();
        });
      },
      { threshold: 0.5 }
    );
    videos.forEach((video) => this.#videoVisibility.observe(video));
  }

  disconnectedCallback() {
    this.#tiles.forEach((tile) => {
      tile.removeEventListener('click', this.#onTileClick);
      tile.removeEventListener('keydown', this.#onTileKeydown);
    });
    this.#showMoreEl?.removeEventListener('click', this.#onShowMoreClick);
    this.#host?.removeEventListener('pdp:variant-update', this.#onVariantUpdate);
    document.body.removeEventListener('breakpoint:pocket', this.#onBreakpointChange);
    this.#destroySwiper();
    this.#videoVisibility?.disconnect();
    this.#videoVisibility = null;
    this.#photoswipe?.close();
    this.#photoswipe = null;
    this.#closeMobileLightbox();
  }

  #onBreakpointChange = (event) => {
    if (event.detail?.matches) this.#initSwiper();
    else this.#destroySwiper();
  };

  #initSwiper() {
    if (this.#swiper || !this.#swiperEl) return;

    let settings = {};
    try {
      settings = JSON.parse(this.#swiperEl.dataset.swiperSettings || '{}');
    } catch (err) {
      console.error('ProductGallery: swiper settings JSON parse failed', err);
    }

    const scrollbarEl = this.querySelector('[data-gallery-scrollbar]');
    if (scrollbarEl) {
      settings.scrollbar = { el: scrollbarEl, draggable: true };
    }

    // new-swiper-exempt: the gallery drives its own breakpoint-gated init/destroy
    // (mobile carousel <=pocket, CSS grid on desktop) with a dynamic scrollbar el,
    // so it manages Swiper directly instead of via the generic <swiper-init>.
    this.#swiper = new Swiper(this.#swiperEl, settings);

    // Design: the last slide is label-free. The labels are a single floating overlay, so
    // flag the swiper when it reaches the end (isEnd = reliable with fractional
    // slidesPerView) and CSS fades them out. !isBeginning keeps the labels on a single
    // locked slide (watchOverflow), where isEnd is also true.
    this.#swiper.on('slideChange', this.#syncLabelsAtEnd);
    this.#swiper.on('toEdge', this.#syncLabelsAtEnd);
    this.#swiper.on('fromEdge', this.#syncLabelsAtEnd);
    this.#syncLabelsAtEnd();
  }

  #syncLabelsAtEnd = () => {
    const atEnd = !!this.#swiper && this.#swiper.isEnd && !this.#swiper.isBeginning;
    this.#swiperEl?.toggleAttribute('data-at-last-slide', atEnd);
  };

  #destroySwiper() {
    if (!this.#swiper) return;
    this.#swiper.destroy(true, true);
    this.#swiper = null;
    this.#swiperEl?.removeAttribute('data-at-last-slide');
  }

  #onVariantUpdate = (event) => {
    const variant = event.detail?.variant;
    if (!variant || !this.#track) return;

    const mediaId = variant.featured_media_id ? String(variant.featured_media_id) : null;
    if (!mediaId) return;

    const tile = this.#track.querySelector(`.main-product__gallery-tile[data-media-id="${mediaId}"]`);
    if (!tile) return;

    const firstTile = this.#track.querySelector('.main-product__gallery-tile');
    if (tile === firstTile) return;

    this.#track.prepend(tile);

    this.#tiles = Array.from(this.#track.querySelectorAll('.main-product__gallery-tile'));
    this.#tiles.forEach((t, i) => {
      t.dataset.index = String(i);
    });
    this.#applyRovingTabindex(this.#tiles[0]);

    if (this.#swiper) {
      this.#swiper.updateSlides();
      this.#swiper.updateProgress();
      this.#swiper.slideTo(0, 0);
    } else {
      this.#track.scrollLeft = 0;
    }
  };

  // Roving tabindex: exactly one tile stays in the Tab order; arrows/Home/End move
  // focus between visible tiles (skips show-more-collapsed display:none ones).
  #applyRovingTabindex(activeTile) {
    this.#tiles.forEach((tile) => {
      tile.setAttribute('tabindex', tile === activeTile ? '0' : '-1');
    });
  }

  #onTileKeydown = (event) => {
    // Video tiles are <div role="button"> (a <video> can't live in a <button>), so
    // Enter/Space activation isn't native — wire it up here. Image/external are
    // real <button>s and activate natively.
    if ((event.key === 'Enter' || event.key === ' ') && event.currentTarget.getAttribute('role') === 'button') {
      event.preventDefault();
      this.#activateTile(event.currentTarget);
      return;
    }

    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(event.key in step) && event.key !== 'Home' && event.key !== 'End') return;

    const visible = this.#tiles.filter((tile) => tile.offsetParent !== null);
    const current = visible.indexOf(event.currentTarget);
    if (current === -1) return;
    event.preventDefault();

    let nextIndex = current + (step[event.key] ?? 0);
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = visible.length - 1;
    nextIndex = Math.min(Math.max(nextIndex, 0), visible.length - 1);

    const next = visible[nextIndex];
    this.#applyRovingTabindex(next);
    next.focus();
    // Mobile carousel: bring the focused slide into view (desktop = CSS grid, no swiper).
    this.#swiper?.slideTo(this.#tiles.indexOf(next));
  };

  #onTileClick = (event) => this.#activateTile(event.currentTarget);

  #activateTile(tile) {
    // "Enable image lightbox" (section setting) off: gallery images are not clickable -
    // no fullscreen view opens on either breakpoint.
    if (this.dataset.enableLightbox === 'false') return;

    // 3D model: interactive in-tile via the load-3d button (model-viewer), no lightbox.
    if (tile.dataset.mediaType === 'model') return;

    // Touch/mobile (<=1024): single vertical-scroll feed for every media type.
    if (responsive.matchesBreakpoint('pocket')) {
      this.#mlbOpener = tile;
      this.#openMobileLightbox(parseInt(tile.dataset.index, 10) || 0);
      return;
    }
    // Desktop: PhotoSwipe holds every media type (video/external play via an
    // injected <video>/<iframe> over their poster slide), so you can page through
    // images and reach the video in the same lightbox.
    this.#openLightbox(tile);
  }

  #onShowMoreClick = () => {
    this.classList.toggle('is-expanded');
    this.#updateShowMoreText();
  };

  #updateShowMoreText() {
    const btn = this.#showMoreEl;
    if (!btn) return;
    const isExpanded = this.classList.contains('is-expanded');
    const newText = isExpanded ? btn.dataset.textClose : btn.dataset.textShow;
    if (!newText) return;
    btn.querySelectorAll('.button-txt').forEach((el) => {
      el.textContent = newText;
    });
  }

  #onPrev = () => this.#photoswipe?.prev();
  #onNext = () => this.#photoswipe?.next();
  #onClose = () => this.#photoswipe?.close();

  async #openLightbox(startTile) {
    // global-scope: .pswp is a section-level singleton rendered outside this element
    const pswpElement = document.querySelector('.pswp');
    if (!pswpElement) {
      console.error('PhotoSwipe template (.pswp) not found in DOM');
      return;
    }

    // Lazy-load PhotoSwipe (v4 UMD shimmed to ES module) on first lightbox open.
    let PhotoSwipe;
    try {
      ({ default: PhotoSwipe } = await import('@theme/photoswipe'));
    } catch (error) {
      console.error('PhotoSwipe failed to load', error);
      return;
    }

    // Every media tile is a lightbox slide. PhotoSwipe v4 can only size IMAGE
    // slides, so video/external use their poster image as the slide (gives pswp the
    // dimensions to size + position), and a <video>/<iframe> is injected over that
    // poster on the active slide (see #syncPswpVideo). Model tiles never open a lightbox.
    this.#pswpTiles = this.#tiles.filter((t) => (t.dataset.mediaType || 'image') !== 'model');
    const startIndex = Math.max(0, this.#pswpTiles.indexOf(startTile));
    const items = this.#pswpTiles.map((tile) => {
      const img = tile.querySelector('.main-product__gallery-image, .main-product__gallery-video');
      const poster = tile.dataset.poster || img?.poster || img?.currentSrc || img?.src || '';
      const type = tile.dataset.mediaType || 'image';
      const w = parseInt(tile.dataset.maxWidth, 10) || 1600;
      const h = parseInt(tile.dataset.maxHeight, 10) || 1600;
      if (type === 'video') {
        return { src: poster, msrc: poster, w, h, vType: 'video', vSrc: tile.dataset.videoSrc || '' };
      }
      if (type === 'external_video') {
        return {
          src: poster, msrc: poster, w, h,
          vType: 'external_video',
          vHost: tile.dataset.externalHost || '',
          vId: tile.dataset.externalId || '',
        };
      }
      return { src: tile.dataset.originalSrc || poster, msrc: poster, w, h };
    });
    const options = {
      index: startIndex,
      showHideOpacity: true,
      showAnimationDuration: 500,
      loop: false,
      history: false,
      closeOnVerticalDrag: true,
      // Desktop-only path (touch <=1024 uses the vertical feed), so no pan-to-next.
      allowPanToNext: false,
      pinchToClose: false,
      errorMsg: '<p class="pswp__error-msg"></p>',
      scaleMode: 'fit',
      maxSpreadZoom: 2,
      getDoubleTapZoom: (isMouseClick, item) => {
        if (isMouseClick) return 1.35;
        return item.initialZoomLevel < 0.7 ? 1 : 1.33;
      },
      getThumbBoundsFn: (idx) => this.#getThumbBounds(idx) || undefined,
    };

    this.#photoswipe = new PhotoSwipe(pswpElement, false, items, options);

    // Lock page scroll for the lightbox lifetime: keeps the scrollbar from
    // toggling on zoom, which would otherwise shift the vw-anchored arrows.
    document.documentElement.classList.add('pswp-lock');

    const prevEl = pswpElement.querySelector('.pswp__button--prev');
    const nextEl = pswpElement.querySelector('.pswp__button--next');
    const closeEl = pswpElement.querySelector('.pswp__button--close');
    const uiEl = pswpElement.querySelector('.pswp__ui');
    const scrollWrap = pswpElement.querySelector('.pswp__scroll-wrap');

    prevEl?.addEventListener('click', this.#onPrev);
    nextEl?.addEventListener('click', this.#onNext);
    closeEl?.addEventListener('click', this.#onClose);

    this.#photoswipe.listen('beforeChange', () => {
      this.#syncNavDisabled();
      this.#clearPswpVideo(); // pause + remove the video of the slide we're leaving
    });

    // Inject (and autoplay) the video/iframe over the poster of the active slide.
    this.#photoswipe.listen('afterChange', () => this.#syncPswpVideo());

    this.#photoswipe.listen('initialZoomIn', () => {
      uiEl?.classList.remove('pswp__ui--hidden');
    });
    this.#photoswipe.listen('initialZoomOut', () => {
      uiEl?.classList.add('pswp__ui--hidden');
    });

    // PhotoSwipe v4 dispatches 'pswpTap' DOM event on tap.
    // Desktop mouse click on image → toggle desktop zoom.
    // Touch single tap → toggle UI visibility (matches happy-ears).
    const onPswpTap = (event) => {
      const detail = event.detail || {};
      const isMouse = !detail.pointerType || detail.pointerType === 'mouse';
      if (isMouse) {
        if (event.target.classList.contains('pswp__img')) {
          this.#photoswipe.toggleDesktopZoom?.(detail.releasePoint);
        }
      } else {
        if (event.target.classList.contains('pswp__button')) return;
        uiEl?.classList.toggle('pswp__ui--hidden');
      }
    };
    scrollWrap?.addEventListener('pswpTap', onPswpTap);

    // Double-tap zoom toggle (touch + mouse double-click)
    this.#photoswipe.listen('doubleTap', (point) => {
      const initialZoom = this.#photoswipe.currItem?.initialZoomLevel;
      if (initialZoom == null) return;
      if (this.#photoswipe.getZoomLevel() !== initialZoom) {
        this.#photoswipe.zoomTo(initialZoom, point, 333);
      } else {
        this.#photoswipe.zoomTo(initialZoom < 0.7 ? 1 : 1.33, point, 333);
      }
    });

    this.#photoswipe.listen('destroy', () => {
      prevEl?.removeEventListener('click', this.#onPrev);
      nextEl?.removeEventListener('click', this.#onNext);
      closeEl?.removeEventListener('click', this.#onClose);
      scrollWrap?.removeEventListener('pswpTap', onPswpTap);
      this.#clearPswpVideo();
      document.documentElement.classList.remove('pswp-lock');
      this.#photoswipe = null;
    });

    this.#photoswipe.init();
    this.#syncNavDisabled();
    // pswp doesn't fire afterChange on init, so inject the opening slide's video
    // once its zoom-wrap is in the DOM (next frame).
    requestAnimationFrame(() => this.#syncPswpVideo());
  }

  // PhotoSwipe video bridge: the active slide is the poster image (so pswp can size
  // and page through it like any image); a centered <video>/<iframe> overlay is laid
  // over the lightbox and plays. The overlay is pointer-events:none except the player
  // itself, so the prev/next/close UI underneath stays clickable.
  #syncPswpVideo() {
    if (!this.#photoswipe) return;
    this.#clearPswpVideo();
    const item = this.#photoswipe.currItem;
    if (!item?.vType) return;
    const pswpElement = document.querySelector('.pswp');
    if (!pswpElement) return;

    let media;
    if (item.vType === 'video' && item.vSrc) {
      media = document.createElement('video');
      media.className = 'pswp__video';
      // Set the aspect ratio up front (from the poster dims) so the box is sized
      // correctly on the first frame - without it the video briefly renders at max-width
      // before max-height constrains it, causing a big->small flash on slide change.
      media.style.aspectRatio = `${item.w} / ${item.h}`;
      media.playsInline = true;
      media.autoplay = true;
      if (item.src) media.poster = item.src;
      const source = document.createElement('source');
      source.src = item.vSrc;
      source.type = 'video/mp4';
      media.appendChild(source);
    } else if (item.vType === 'external_video' && item.vId) {
      const id = encodeURIComponent(item.vId);
      media = document.createElement('iframe');
      media.className = 'pswp__video pswp__video--iframe';
      media.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
      media.setAttribute('allowfullscreen', '');
      media.style.setProperty('--ar', `${item.w} / ${item.h}`);
      media.src =
        item.vHost === 'youtube'
          ? `https://www.youtube.com/embed/${id}?playsinline=1&rel=0&autoplay=1`
          : `https://player.vimeo.com/video/${id}?autoplay=1`;
    }
    if (!media) return;

    const overlay = document.createElement('div');
    overlay.className = 'pswp__video-overlay';
    if (item.vType === 'video') {
      // Wrap the <video> + custom control bar (same UI as the hero) in a positioned box.
      const wrap = document.createElement('div');
      wrap.className = 'pswp__video-wrap';
      wrap.appendChild(media);
      // "Controls" (section setting) off -> no custom control bar on the lightbox video.
      const controls =
        this.dataset.videoControls !== 'false' ? this.#attachVideoControls(media) : null;
      if (controls) wrap.appendChild(controls);
      overlay.appendChild(wrap);
    } else {
      overlay.appendChild(media);
    }
    pswpElement.appendChild(overlay);
    // Hide the poster image slide underneath: it can be a different aspect (square
    // default dims) upscaled to fill the viewport, so it shows around the video as a
    // blurry frame. The video carries its own poster, so there is no gap.
    pswpElement.classList.add('pswp--video-active');
    media.play?.().catch(() => {});
  }

  #clearPswpVideo() {
    const pswpElement = document.querySelector('.pswp');
    pswpElement?.classList.remove('pswp--video-active');
    pswpElement?.querySelectorAll('.pswp__video-overlay').forEach((el) => {
      el.querySelector('video')?.pause?.();
      el.remove();
    });
  }

  // Build + wire the custom control bar (cloned from the #pdp-video-controls template)
  // for a plain lightbox <video>, so gallery videos get the same UI as the hero video
  // (aria-label-driven .section-video__controls CSS; no video.js). Returns the controls
  // element, or null if the template is missing.
  #attachVideoControls(video) {
    const tpl = document.getElementById('pdp-video-controls');
    if (!tpl?.content?.firstElementChild) return null;
    const controls = tpl.content.firstElementChild.cloneNode(true);
    const state = controls.querySelector('[data-action="toggle-state"]');
    const sound = controls.querySelector('[data-action="toggle-sound"]');
    const progress = controls.querySelector('[data-video-progress]');
    const track = controls.querySelector('[data-video-progress-track]');
    const fill = controls.querySelector('[data-video-progress-fill]');
    const thumb = controls.querySelector('[data-video-progress-thumb]');

    const syncState = () => state?.setAttribute('aria-label', video.paused ? 'paused' : 'playing');
    const syncSound = () => sound?.setAttribute('aria-label', video.muted ? 'muted' : 'unmuted');
    const syncProgress = () => {
      const pct = video.duration ? (video.currentTime / video.duration) * 100 : 0;
      if (fill) fill.style.width = `${pct}%`;
      if (thumb) thumb.style.left = `${pct}%`;
      progress?.setAttribute('aria-valuenow', String(Math.round(pct)));
    };
    const seek = (clientX) => {
      if (!track || !video.duration) return;
      const rect = track.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      video.currentTime = ratio * video.duration;
    };

    state?.addEventListener('click', () => (video.paused ? video.play() : video.pause()));
    sound?.addEventListener('click', () => {
      video.muted = !video.muted;
    });
    track?.addEventListener('click', (event) => seek(event.clientX));
    video.addEventListener('play', syncState);
    video.addEventListener('pause', syncState);
    video.addEventListener('volumechange', syncSound);
    video.addEventListener('timeupdate', syncProgress);
    video.addEventListener('loadedmetadata', syncProgress);

    syncState();
    syncSound();
    syncProgress();
    return controls;
  }

  #syncNavDisabled() {
    if (!this.#photoswipe) return;
    const idx = this.#photoswipe.getCurrentIndex();
    const total = this.#pswpTiles.length;
    // global-scope: pswp UI lives in the section-level .pswp singleton, not in this element
    const prevEl = document.querySelector('.pswp__button--prev');
    const nextEl = document.querySelector('.pswp__button--next');
    const counterEl = document.querySelector('[data-pswp-counter]');
    if (prevEl) {
      if (idx === 0) prevEl.setAttribute('disabled', 'disabled');
      else prevEl.removeAttribute('disabled');
    }
    if (nextEl) {
      if (idx === total - 1) nextEl.setAttribute('disabled', 'disabled');
      else nextEl.removeAttribute('disabled');
    }
    if (counterEl) {
      counterEl.textContent = `${idx + 1}/${total}`;
    }
  }

  #collectItems() {
    return this.#tiles.map((tile) => {
      const img = tile.querySelector('.main-product__gallery-image');
      const poster = img?.currentSrc || img?.src || '';
      const w = parseInt(tile.dataset.maxWidth, 10) || 1600;
      const h = parseInt(tile.dataset.maxHeight, 10) || 1600;
      const type = tile.dataset.mediaType || 'image';

      // Mobile feed builder: `src` is always a still poster; video/external slides
      // also carry `type` + videoSrc/external so the feed can render the player.
      if (type === 'video') {
        return { type, src: tile.dataset.poster || poster, msrc: poster, videoSrc: tile.dataset.videoSrc || '', w, h };
      }
      if (type === 'external_video') {
        return { type, src: poster, msrc: poster, externalHost: tile.dataset.externalHost || '', externalId: tile.dataset.externalId || '', w, h };
      }
      // image (and model preview)
      return { type, src: tile.dataset.originalSrc || poster, msrc: poster, w, h };
    });
  }

  #getThumbBounds(index) {
    const tile = this.#pswpTiles[index];
    if (!tile) return null;
    const img = tile.querySelector('.main-product__gallery-image, .main-product__gallery-video');
    if (!img) return null;
    const rect = img.getBoundingClientRect();
    const pageYScroll = window.scrollY || document.documentElement.scrollTop;
    return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
  }

  // ===== Mobile fullscreen vertical-scroll lightbox =====

  #openMobileLightbox(startIndex) {
    if (this.#mlb) this.#closeMobileLightbox();

    const items = this.#collectItems();
    const total = items.length;

    const slidesHtml = items
      .map((it, i) => {
        const ratio = `style="aspect-ratio:${it.w}/${it.h}"`;
        if (it.type === 'video' && it.videoSrc) {
          return (
            `<div class="pdp-mlb__slide" data-index="${i}">` +
            `<div class="pdp-mlb__video-wrap">` +
            `<video class="pdp-mlb__media" playsinline preload="none" poster="${it.src}" ${ratio}>` +
            `<source src="${it.videoSrc}" type="video/mp4"></video></div></div>`
          );
        }
        if (it.type === 'external_video' && it.externalId) {
          const id = encodeURIComponent(it.externalId);
          const embed =
            it.externalHost === 'youtube'
              ? `https://www.youtube.com/embed/${id}?playsinline=1&rel=0`
              : `https://player.vimeo.com/video/${id}`;
          return (
            `<div class="pdp-mlb__slide" data-index="${i}">` +
            `<iframe class="pdp-mlb__media" src="${embed}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen ${ratio}></iframe></div>`
          );
        }
        return (
          `<div class="pdp-mlb__slide" data-index="${i}">` +
          `<img class="pdp-mlb__img" src="${it.src}" alt="" draggable="false" loading="lazy" ${ratio}></div>`
        );
      })
      .join('');

    const lb = document.createElement('div');
    lb.className = 'pdp-mlb';
    // Mirror <main-product>'s color scheme: the lightbox is appended to <body>, outside the
    // section's colorGroup scope, so its --primary_text / --background_color would otherwise
    // fall back to :root (primary). Matches the .pswp class set in main-product.liquid.
    const mlbScheme = (this.closest('main-product') || document.querySelector('main-product'))
      ?.className.split(' ')
      .find((cls) => cls.startsWith('colorGroup--'));
    if (mlbScheme) lb.classList.add(mlbScheme);
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', this.dataset.i18nDialogLabel || '');
    lb.innerHTML =
      `<button class="pdp-mlb__close" type="button" aria-label="${this.dataset.i18nClose || ''}">` +
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">' +
      '<path d="M19.7992 5.14234L12.9414 12.0002L19.7992 18.8581L18.8571 19.8002L11.9992 12.9423L5.14136 19.8002L4.19922 18.8581L11.0571 12.0002L4.19922 5.14234L5.14136 4.2002L11.9992 11.0581L18.8571 4.2002L19.7992 5.14234Z"/></svg>' +
      '</button>' +
      `<div class="pdp-mlb__scroll" data-mlb-scroll>${slidesHtml}</div>` +
      `<div class="pdp-mlb__counter u-s1"><span class="u-s1" data-mlb-current>${startIndex + 1}</span>/${total}</div>`;

    document.body.appendChild(lb);
    document.body.classList.add('pdp-mlb-open');
    this.#mlb = lb;

    const scrollEl = lb.querySelector('[data-mlb-scroll]');
    const currentEl = lb.querySelector('[data-mlb-current]');
    const slides = Array.from(lb.querySelectorAll('.pdp-mlb__slide'));

    // Custom control bar on each gallery video (same UI as the hero; native controls removed).
    // "Controls" (section setting) off -> no bar.
    if (this.dataset.videoControls !== 'false') {
      lb.querySelectorAll('.pdp-mlb__video-wrap').forEach((wrap) => {
        const media = wrap.querySelector('video');
        const controls = media && this.#attachVideoControls(media);
        if (controls) wrap.appendChild(controls);
      });
    }

    // jump straight to the tapped media
    slides[startIndex]?.scrollIntoView({ block: 'start' });

    // The click that opened the lightbox is a user gesture, so the opened video
    // may autoplay. Closing removes the element (stops playback).
    slides[startIndex]?.querySelector('video')?.play?.().catch(() => {});

    // counter follows whichever slide crosses the viewport centre line
    this.#mlbObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            currentEl.textContent = parseInt(entry.target.dataset.index, 10) + 1;
          }
        });
      },
      { root: scrollEl, rootMargin: '-50% 0px -50% 0px', threshold: 0 }
    );
    slides.forEach((slide) => this.#mlbObserver.observe(slide));

    // Pinch / double-tap zoom on each image in the mobile feed.
    slides.forEach((slide) => this.#bindMobileZoom(slide));

    const closeBtn = lb.querySelector('.pdp-mlb__close');
    closeBtn.addEventListener('click', this.#closeMobileLightbox);
    document.addEventListener('keydown', this.#onMlbKeyDown);
    trapFocus(lb, closeBtn);
  }

  #onMlbKeyDown = (event) => {
    if (event.key === 'Escape') this.#closeMobileLightbox();
  };

  #closeMobileLightbox = () => {
    if (!this.#mlb) return;
    this.#mlbObserver?.disconnect();
    this.#mlbObserver = null;
    document.removeEventListener('keydown', this.#onMlbKeyDown);
    this.#mlb.remove();
    this.#mlb = null;
    document.body.classList.remove('pdp-mlb-open');
    removeTrapFocus(this.#mlbOpener);
    this.#mlbOpener = null;
  };

  // Per-image pinch + double-tap zoom with panning. Scale stays inside the
  // slide box (overflow:hidden); when not zoomed the feed scrolls normally.
  #bindMobileZoom(slide) {
    const img = slide.querySelector('.pdp-mlb__img');
    if (!img) return;

    const MAX = 3;
    const DOUBLE = 2.5;
    let scale = 1;
    let tx = 0;
    let ty = 0;
    let startScale = 1;
    let startDist = 0;
    let focalX = 0;
    let focalY = 0;
    let panOffX = 0;
    let panOffY = 0;
    let mode = null; // 'pinch' | 'pan'
    let lastTap = 0;

    const apply = () => {
      img.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;
    };
    // will-change only while a gesture is active (never static — avoids a
    // permanent compositor layer per feed image).
    const hint = (on) => { img.style.willChange = on ? 'transform' : ''; };
    const animate = (on) => img.classList.toggle('is-animating', on);
    const clamp = () => {
      const w = slide.clientWidth;
      const h = img.clientHeight;
      tx = Math.min(0, Math.max(w * (1 - scale), tx));
      ty = Math.min(0, Math.max(h * (1 - scale), ty));
    };
    const dist = (t) =>
      Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    const reset = () => {
      animate(true);
      scale = 1;
      tx = 0;
      ty = 0;
      apply();
    };

    const onStart = (event) => {
      const rect = slide.getBoundingClientRect();
      if (event.touches.length === 2) {
        mode = 'pinch';
        animate(false);
        hint(true);
        startScale = scale;
        startDist = dist(event.touches);
        const mx = (event.touches[0].clientX + event.touches[1].clientX) / 2 - rect.left;
        const my = (event.touches[0].clientY + event.touches[1].clientY) / 2 - rect.top;
        focalX = (mx - tx) / scale;
        focalY = (my - ty) / scale;
        event.preventDefault();
        return;
      }

      if (event.touches.length === 1) {
        const now = Date.now();
        if (now - lastTap < 300) {
          // double-tap toggle
          event.preventDefault();
          animate(true);
          if (scale > 1) {
            scale = 1;
            tx = 0;
            ty = 0;
          } else {
            const px = event.touches[0].clientX - rect.left;
            const py = event.touches[0].clientY - rect.top;
            scale = DOUBLE;
            tx = px - scale * px;
            ty = py - scale * py;
            clamp();
          }
          apply();
          lastTap = 0;
          return;
        }
        lastTap = now;

        if (scale > 1) {
          mode = 'pan';
          animate(false);
          hint(true);
          panOffX = event.touches[0].clientX - tx;
          panOffY = event.touches[0].clientY - ty;
          event.preventDefault();
        }
      }
    };

    const onMove = (event) => {
      if (mode === 'pinch' && event.touches.length === 2) {
        event.preventDefault();
        const rect = slide.getBoundingClientRect();
        scale = Math.min(MAX, Math.max(1, startScale * (dist(event.touches) / startDist)));
        const mx = (event.touches[0].clientX + event.touches[1].clientX) / 2 - rect.left;
        const my = (event.touches[0].clientY + event.touches[1].clientY) / 2 - rect.top;
        tx = mx - scale * focalX;
        ty = my - scale * focalY;
        clamp();
        apply();
      } else if (mode === 'pan' && event.touches.length === 1 && scale > 1) {
        event.preventDefault();
        tx = event.touches[0].clientX - panOffX;
        ty = event.touches[0].clientY - panOffY;
        clamp();
        apply();
      }
    };

    const onEnd = (event) => {
      if (event.touches.length === 0) {
        if (scale <= 1.02) reset();
        mode = null;
        hint(false);
      } else if (event.touches.length === 1 && scale > 1) {
        // a finger lifted after pinch -> continue as pan
        mode = 'pan';
        panOffX = event.touches[0].clientX - tx;
        panOffY = event.touches[0].clientY - ty;
      }
    };

    slide.addEventListener('touchstart', onStart, { passive: false });
    slide.addEventListener('touchmove', onMove, { passive: false });
    slide.addEventListener('touchend', onEnd);
    slide.addEventListener('touchcancel', onEnd);
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}
